import React, { useContext, useEffect, useState } from 'react';
import { PlusIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Divider } from '../../../components/Divider';
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination'; // Import the Pagination component
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { UserContext } from '../../../context/userContext';
import { useWarehouseProductCategory } from '../../../hooks/useWarehouseProductCategory';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { Text } from '../../../components/Text';

const COLUMNS = [
    { key: 'code', label: 'Category Code' },
    { key: 'department', label: 'Department' },
    { key: 'created', label: 'Created on' },
];
const SORT_OPTIONS = [
    { label: 'Name (A-Z)', value: 'az' },
    { label: 'Name (Z-A)', value: 'za' },
];

const WarehouseProductCategoryPage: React.FC = () => {
    const { currentUser } = useContext(UserContext); // Get current user info
    const { loading, error, deleteCategory, fetchCategories, categories } = useWarehouseProductCategory();
    const [searchQuery, setSearchQuery] = useState<string>(''); // State to store the search query
    const [isOpen, setIsOpen] = useState(false); // Dialog open state
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion
    const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState('az');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterDepartmentName, setFilterDepartmentName] = useState('');

    useEffect(() => {
        document.title = "Product Categories";
    }, []);

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterDepartmentName('');
    };

    const availableYears = Array.from(
        new Set(
            categories
                .map((c) => new Date(c.created_time || '').getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const availableDepartmentNames = Array.from(
        new Set(categories.map((c) => c.department_name).filter((name): name is string => !!name))
    ).sort();

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterDepartmentName].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterDepartmentName ? [{ key: 'department', label: `Department: ${filterDepartmentName}`, onClear: () => setFilterDepartmentName('') }] : []),
    ];

    const filteredCategories = categories.filter((category) => {
        const matchesSearch = category.category_name!.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStartDate = !filterStartDate || new Date(category.created_time || '') >= new Date(filterStartDate);
        const matchesEndDate = !filterEndDate || new Date(category.created_time || '') <= new Date(filterEndDate);
        const matchesYear = !filterYear || new Date(category.created_time || '').getFullYear().toString() === filterYear;
        const matchesDepartment = !filterDepartmentName || category.department_name === filterDepartmentName;
        return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesDepartment;
    });

    const handleOpenDeleteDialog = (categoryId: string) => {
        setSelectedCategoryId(categoryId);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedCategoryId) return;

        setIsDeleting(true);
        try {
            await deleteCategory(Number(selectedCategoryId));
            fetchCategories();
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete category:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Product Categories</Heading>
                </div>
                <Button onClick={() => navigate('/gp/inventories/categories/add')}><PlusIcon />Create new category</Button>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search category…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    <TableHeader>Category Name</TableHeader>
                                    {isColumnVisible(columnVisibility, 'code') && <TableHeader>Category Code</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'department') && <TableHeader>Department</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created on</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredCategories.length > 0) ? [...filteredCategories].sort((a, b) => sortOrder === 'az' ? a.category_name!.localeCompare(b.category_name!) : b.category_name!.localeCompare(a.category_name!)).map((category, index) => (
                                    <TableRow key={category.id} title={`${category.category_name}`}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{category.category_name}</TableCell>
                                        {isColumnVisible(columnVisibility, 'code') && <TableCell className="text-zinc-500">{category.category_code}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'department') && <TableCell className="text-zinc-500">{category.department_name}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell className="text-zinc-500">{dateFormat(category.created_time?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        <TableCell className="py-5 flex gap-4 text-zinc-500 justify-center">
                                            <a onClick={() => navigate(`/gp/inventories/categories/edit/${category.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <PencilIcon className='h-5 w-5' />
                                            </a>
                                            <a onClick={() => navigate(`/gp/inventories/categories/view/${category.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className='h-5 w-5' >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => handleOpenDeleteDialog(category.id.toString())}>Delete</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No category found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {availableDepartmentNames.length > 0 && (
                    <div>
                        <Text>By Department</Text>
                        <Select value={filterDepartmentName} onChange={(e) => setFilterDepartmentName(e.target.value)}>
                            <option value="">All Departments</option>
                            {availableDepartmentNames.map((name) => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Product Category</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this category? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WarehouseProductCategoryPage;
